
import {
  FeatureSource,
  Feature,
} from "@models";
import {
  Campaign,
  CharacterFeat,
  CharacterFeatureBase,
  CharacterWeapon,
  CharacterPactBoon,
  CharacterSubrace,
  CharacterSpecialFeature,
  CharacterSpell,
  Spell,
  CharacterBackground,
  Item,
  BasicItem,
  MagicItemKeyword,
  PseudoItem,
  Feat,
  Armor,
  Weapon,
  CharacterClass,
  CharacterSubclass,
  GameClass,
  CharacterGift,
  Gift,
  Background,
  CharacterArmor,
  CharacterItem,
  BasicCreatureInstance,
  Creature,
  CharacterBasicItem,
  SummonInstance, 
  Encounter,
  CreatureSpellAsAbility,
  SubclassFeature,
  Character,
  CharacterCustomItem,
  CharacterContainer,
  Ammunition,
  CharacterAmmunition,
  Transform,
  TransformInstance,
  CharacterDeck,
  CharacterGem,
  GemItem,
  CharacterBook,
  BastionFacility,
  CharacterBastionFacility,
  ComplexCreatureInstance,
  Ability,
  CampaignChar,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DataUtilities from "./data_utilities";

const api = API.getInstance();
const data_util = DataUtilities.getInstance();

export class CharConnectUtilitiesClass {
  connectCharacter = async (
    char: Character | ComplexCreatureInstance, 
    onProgress?: ((data_type: string, percentage: number ) => void),
    onComplete?: () => Promise<void>
  ) => {
    // debugger;
    if (char instanceof Character) {
      if (char.sidekick) {
        await this.connectSidekickCreature(char);
      }
      await this.connectStandardFeatures(char, onProgress);
      await this.connectRace(char, onProgress);
      await this.connectGifts(char, onProgress);
      await this.connectConditions(char, onProgress);
      await this.connectBastionFacilities(char, onProgress);
      await this.connectCampaign(char, onProgress);
      await this.connectBackground(char, onProgress);
      await this.connectClasses(char, onProgress);
      await this.connectItems(char, onProgress);
      await this.connectExtraFeats(char, onProgress);
      // These two need to be done again after char.features is populated
      await this.connectMinions(char, onProgress); 
      await this.connectTransform(char);
      
      await this.connectNotes(char, onProgress);
      await this.connectSpells(char, onProgress);
      char.connected = true;
      if (onProgress) {
        onProgress("Processing", 0);
      }
      if (onComplete) {
        await onComplete(); // The purpose of this is to make it so when it recalculates it will await
      } else {
        char.subscription_holder.recalculate();
      }
    } else {
      debugger;
    }
  }
  connectNotes = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const max = char.all_char_feature_sources.length;
    let complete = 0;
    const doProgress = () => {
      if (onProgress) {
        onProgress("Notes", complete / max);
      }
    }
    doProgress();
    for (const cfs of char.all_char_feature_sources) {
      if (cfs.source) {
        if (cfs.source.notes.length > 0) {
          cfs.notes = cfs.source.notes;
        } else if (cfs.source.variant_group.length > 1) {
          const variant_of = data_util.getDominantFromObject(cfs.source);
          if (variant_of instanceof FeatureSource) {
            cfs.notes = variant_of.notes;
          }
        }
      }
      complete++;
      doProgress();
    }
  }
  connectSpells = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    for (const char_spell of char.spells) {
      if (!char_spell.spell) {
        const spell = data_util.getSpell(char_spell.spell_id);
        if (spell) {
          char_spell.connectSpell(spell);
        }
      }
    }
    for (const char_spell of char.spellbook_spells) {
      if (!char_spell.spell) {
        const spell = data_util.getSpell(char_spell.spell_id);
        if (spell) {
          char_spell.connectSpell(spell);
        }
      }
    }
  }
  connectRace = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const doProgress = () => {
      if (onProgress) {
        onProgress("Race", 0);
      }
    }
    doProgress();
    if (!char.race.source) {
      const race = data_util.races.find(o => o._id === char.race.source_id);
      if (!race && char.race.source_id !== "") {
        console.error(data_util.races);
        console.error("Race disappearing");
      }
      if (race) {
        await data_util.loadObject(race);
        // debugger;
        await char.race.copy_source({ char, feature_source: race });
        char.race.table = await data_util.getTableAsync(race);
        if (race.sizes.includes(7)) { // "Size from Subrace"
        } else if (race.sizes.length > 0 && !race.sizes.includes(char.base_size)) {
          char.base_size = race.sizes[0];
        }
        const subrace_fb_finder = char.race.char_feature_bases.filter(o => o.features.filter(o2 => o2.feature_type === "Subrace").length === 1);
        if (subrace_fb_finder.length > 1) {
          for (const scfb of subrace_fb_finder) {
            await this.connectFeatureBase(scfb, char);
          }
        }
        if (subrace_fb_finder.length === 1) {
          const subrace_feature_finder = subrace_fb_finder[0].features.filter(o2 => o2.feature_type === "Subrace");
          if (subrace_feature_finder.length === 1) {
            const subrace_feature = subrace_feature_finder[0];
            if ((subrace_feature.feature_options.length === 0 || 
              subrace_feature.feature_options[0] === null || 
              (subrace_feature.feature_options[0] as CharacterSubrace).source_id === "") && 
              char.race.old_subrace) {
              subrace_feature.feature_options[0] = char.race.old_subrace;
            }
            if (subrace_feature.feature_options.length > 0 && 
              subrace_feature.feature_options[0] !== null) {
              const char_subrace = subrace_feature.feature_options[0] as CharacterSubclass;
              const scfs_finder = char.race.sub_char_feature_sources.filter(o => o.source_id === char_subrace.source_id);
              if (scfs_finder.length === 0) {
                char.race.sub_char_feature_sources.push(char_subrace);
                char.race.subrace_id = char_subrace.source_id;
              }
            }
          }
        }
        if (char.race.subrace_id !== "") {
          const subrace_finder = data_util.subraces.find(o => o._id === char.race.subrace_id);
      
          if (subrace_finder) {
            const subrace = subrace_finder;
            await data_util.loadObject(subrace);
            if (race.sizes.includes(7)) { // "Size from Subrace"
              if (subrace.sizes.length > 0 && !subrace.sizes.includes(char.base_size)) {
                char.base_size = subrace.sizes[0];
              }
            }
            char.race.connect_subrace(char, subrace);
          } else {
            char.race.subrace_id = "";
          }
        }
        for (let i = 0; i < char.race.all_char_feature_bases.length; ++i) {
          const cfb = char.race.all_char_feature_bases[i];
          await this.connectFeatureBase(cfb, char);
        }
      } else {
        char.race.source_id = "";
      }
    }
  }
  connectSidekickCreature = async (char: Character) => {
    if (!char.sidekick_creature && char.sidekick_creature_id !== "") {
      const res = await api.getFullObject("creature", char.sidekick_creature_id);
      if (res instanceof Creature) {
        char.sidekick_creature = res;
        const ci = new BasicCreatureInstance();
        await ci.copyCreature(res, res.challenge_rating, true);
        char.base_ability_scores = ci.ability_scores;
        char.sidekick_ci = ci;
        for (const condition_id of ci.condition_immunities) {
          if (!char.condition_immunities.includes(condition_id)) {
            char.condition_immunities.push(condition_id);
          }
        }
      }
    }
  }
  connectGifts = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.gifts.length > 0) {
      const max = char.gifts.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Gifts", complete / max);
        }
      }
      doProgress();
      const gifts = await data_util.getGifts();
      for (const char_gift of char.gifts) {
        const gift = gifts.find(o => o._id === char_gift.source_id);
        if (gift) {
          await data_util.loadObject(gift);
          await char_gift.copy_source({ char, feature_source: gift });
          char_gift.table = await data_util.getTableAsync(gift); 
          for (const cfb of char_gift.all_char_feature_bases) {
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          remove_us.push(char_gift.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectConditions = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.conditions.length > 0) {
      const max = char.conditions.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Conditions", complete / max);
        }
      }
      doProgress();
      const conditions = await data_util.getConditions();
      for (const char_cond of char.conditions) {
        const cond = conditions.find(o => o._id === char_cond.source_id);
        if (cond) {
          await data_util.loadObject(cond);
          await char_cond.copy_source({ char, feature_source: cond });
          char_cond.table = await data_util.getTableAsync(cond);
          for (const cfb of char_cond.all_char_feature_bases) {
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          remove_us.push(char_cond.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectBastionFacilities = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.bastion_facilities.length > 0) {
      const max = char.spells.length + char.spellbook_spells.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Bastion Facilities", complete / max);
        }
      }
      doProgress();
      const bastion_facilities = await data_util.getBastionFacilities();
      for (let k = 0; k < char.bastion_facilities.length; ++k) {
        const char_bastion_facility = char.bastion_facilities[k];
        const bastion_facility = bastion_facilities.find(o => o._id === char_bastion_facility.source_id);
        if (bastion_facility) {
          await data_util.loadObject(bastion_facility);
          char_bastion_facility.source = bastion_facility;
        } else {
          remove_us.push(char_bastion_facility.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectCampaign = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number) => void)) => {
    // debugger;
    if (!char.c_char || !char.c_char.campaign) {
      let c_char = await api.getCampaignObject<CampaignChar>({ data_type: "campaign_char", filter: { char_id: char._id }});
      if (c_char) {
        const doProgress = (data_type: string, percentage: number) => {
          if (onProgress) {
            onProgress(data_type, percentage);
          }
        }
        doProgress("Campaign", 0);
        const campaign = await api.getCampaign({ campaign_id: c_char.campaign_id, include: ["players","characters"], onProgress });
        if (campaign) {
          const c_char_id = c_char._id;
          c_char = campaign.c_chars.find(o => o._id === c_char_id);
        }
        if (c_char) {
          char.c_char = c_char;
        }
      }
    }
  }
  connectStandardFeatures = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (!char.standard_features.source && char.character_options.standard_features_id !== "") {
      const doProgress = () => {
        if (onProgress) {
          onProgress("Standard Features", 0);
        }
      }
      doProgress();
      const standard_features = data_util.standard_features.find(o => o._id === char.character_options.standard_features_id);
      if (standard_features) {
        await data_util.loadObject(standard_features);
        await char.standard_features.copy_source({ char, feature_source: standard_features });
      }
    }
  }
  connectBackground = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (!char.background.source && char.background.source_id !== "") {
      const doProgress = () => {
        if (onProgress) {
          onProgress("Background", 0);
        }
      }
      doProgress();
      // debugger;
      const obj_finder = data_util.backgrounds.find(o => o._id === char.background.source_id);
      if (obj_finder) {
        const background = obj_finder;
        await data_util.loadObject(background);
        await char.background.copy_source({ char, feature_source: background });
        // No Backgrounds currently use Tables
        for (let i = 0; i < char.background.all_char_feature_bases.length; ++i) {
          await this.connectFeatureBase(char.background.all_char_feature_bases[i], char);
        }
      } else {
        char.background.source_id = "";
      }
    }
  }
  connectClasses = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (char.classes.length > 0) {
      
      const max = char.classes.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Classes", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < char.classes.length; ++k) {
        const char_class = char.classes[k];
        const class_finder = data_util.classes.find(o => o._id === char_class.source_id);
        if (class_finder) {
          const game_class = class_finder;
          // debugger;
          await data_util.loadObject(game_class);
          char_class.table = await data_util.getTableAsync(game_class);
          await char_class.copy_source({ char, feature_source: game_class });
        }
        const subclass_fb_finder = char_class.char_feature_bases.filter(o => 
          o.features.filter(o2 => 
            o2.floating_feature instanceof SubclassFeature && 
            o2.floating_feature.level === 1).length === 1);
        if (subclass_fb_finder.length > 1) {
          for (const scfb of subclass_fb_finder) {
            await this.connectFeatureBase(scfb, char);
          }
        }
        if (subclass_fb_finder.length === 1) {
          const subclass_feature_finder = subclass_fb_finder[0].features.filter(o2 => o2.feature_type === "Subclass");
          if (subclass_feature_finder.length === 1) {
            const subclass_feature = subclass_feature_finder[0];
            if ((subclass_feature.feature_options.length === 0 || 
              subclass_feature.feature_options[0] === null) && char_class.old_char_subclass) {
              subclass_feature.feature_options[0] = char_class.old_char_subclass;
            }
            if (subclass_feature.feature_options.length > 0 && 
              subclass_feature.feature_options[0] !== null) {
              const char_subclass = subclass_feature.feature_options[0] as CharacterSubclass;
              const scfs_finder = char_class.sub_char_feature_sources.filter(o => o.source_id === char_subclass.source_id);
              if (scfs_finder.length === 0) {
                char_class.sub_char_feature_sources.push(char_subclass);
              }
            }
          }
        }
        const subclass_finder = data_util.subclasses.find(o => o._id === char_class.subclass_id);
        if (subclass_finder) {
          const subclass = subclass_finder;
          await data_util.loadObject(subclass);
          char_class.connect_subclass(char, subclass);
          if (char_class.char_subclass) {
            char_class.char_subclass.table = await data_util.getTableAsync(subclass);
          }
          for (let i = 0; i < char_class.all_char_feature_bases.length; ++i) {
            const cfb = char_class.all_char_feature_bases[i];
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          char_class.sub_char_feature_sources = char_class.sub_char_feature_sources.filter(o => o.source_id !== char_class.subclass_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectItems = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    // debugger;
    const max = char.items.length;
    let complete = 0;
    const doProgress = () => {
      if (onProgress) {
        onProgress("Items", complete / max);
      }
    }
    doProgress();
    for (let k = 0; k < char.items.length; ++k) {
      const char_item = char.items[k];
      // if (char_item.base_item_id === "600121bbec43802088716283") {
      //   debugger;
      // }
      await this.connectItem(char, char_item);
      complete++;
      doProgress();
    }
    if (char.items.filter(o => o.name !== "Equipment" && o.name !== "Storage").length > 0 && 
      !char.items.find(o => o.base_item && o.base_item.name === "Backpack")) {
      const backpack = data_util.base_pseudo_items.find(o => o.name === "Backpack");
      if (backpack) {
        // debugger;
        await this.addItem({ char, item: backpack });
      }
    }
    for (const cbf of char.bastion_facilities) {
      for (const char_item of cbf.items) {
        await this.connectItem(char, char_item);
      }
      const creatures: Creature[] = [];
      for (const ci of cbf.creatures) {
        await this.connectCreature(ci, creatures);
      }
    }
    for (const component of char.recipe_components.filter(o => o.creature_details._id !== "")) {
      const res = await api.getFullObject("creature", component.creature_details._id);
      if (res instanceof Creature) {
        component.creature = res;
      }
    }
  }
  connectItem = async (char: Character, char_item: CharacterItem) => {
    // if (char_item.name.includes("Scroll")) {
    //   debugger;
    // }
    // debugger;
    if (char_item.base_item_id !== "") {
      const base_item = data_util.items.find(o => o._id === char_item.base_item_id);
      if (base_item) {
        await data_util.loadObject(base_item);
        await char_item.connectBaseItem({ owner: char, base_item });
        if (base_item instanceof Weapon && 
          char_item instanceof CharacterWeapon && 
          char_item.using_attack_types.length === 0) {
          char_item.using_attack_types = [...base_item.the_attack_types];
        }
      } else {
        console.log(data_util.items.length);
        console.log(char_item);
      }
    }
    if (char_item.extra !== "") {
      // debugger;
      let spell = data_util.spells.find(o => o._id === char_item.extra);
      if (!spell) {
        // It's probably archived.  Try to find variants, and take the dominant one.
        spell = data_util.getDominantSpell(char_item.extra);
      }
      if (spell) {
        await data_util.loadObject(spell);
        char_item.extra_spell = spell;
        char_item.extra = spell._id;
      }
    }
    for (const cmik of char_item.char_magic_item_keywords) {
      const magic_item_keyword = data_util.magic_item_keywords.find(o => o._id === cmik.source_id);
      if (magic_item_keyword) {
        await data_util.loadObject(magic_item_keyword);
        await this.connectMagicItemKeyword({
          char, 
          char_item_or_id: char_item, 
          magic_item_keyword_or_id: magic_item_keyword,
          char_feature_id: ""
        });
      }
    }
  }
  connectExtraFeats = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (char.extra_feats.length > 0) {
      const max = char.extra_feats.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Feats", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < char.extra_feats.length; ++k) {
        const char_feat = char.extra_feats[k];
        if (char_feat.source_id !== "") {
          const feat = data_util.feats.find(o => o._id === char_feat.source_id);
          if (feat) {
            await data_util.loadObject(feat);
            await char_feat.copy_source({ char, feature_source: feat, item_id: char_feat.item_id });
          }
          if (char.feats.filter(o => o.true_id === char_feat.true_id).length === 0) {
            char.feats.push(char_feat);
          }
        }
        complete++;
        doProgress();
      }
    }
  }
  connectMinions = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (char.minions.length > 0) {
      const max = char.minions.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Minions", complete / max);
        }
      }
      doProgress();
      const creatures: Creature[] = [];
      for (let k = 0; k < char.minions.length; ++k) {
        const minion = char.minions[k];
        if (!minion.connected) {
          if (minion instanceof BasicCreatureInstance) {
            if (!minion.connected && minion.creature_id !== "") {
              let creature = creatures.find(o => o._id === minion.creature_id);
              if (!creature) {
                const res = await api.getFullObject("creature", minion.creature_id);
                if (res instanceof Creature) {
                  creatures.push(res);
                  creature = res;
                  for (const fb of creature.fbs) {
                    for (const f of fb.features) {
                      await this.connectCreatureAction(f);
                    }
                  }
                }
              }
              if (creature) {
                await minion.connectCreature(creature, true);
              }
            } 
            minion.connected = true;
          } else if (minion instanceof SummonInstance) {
            const cf = char.features.find(o => o.true_id === minion.cf_true_id);
            if (cf && cf.floating_feature) {
              // debugger;
              if (cf.floating_feature instanceof Ability) {
                const effect = cf.floating_feature.effects.find(o => 
                  o.type === "Summon" && 
                  o.potences.some(o2 => o2.summon_options.some(o3 => 
                    o3.custom_stat_block && 
                    o3.custom_stat_block.true_id === minion.stat_block_id)));
                if (effect) {
                  const potence = effect.potences.find(o2 => o2.summon_options.some(o3 => 
                    o3.custom_stat_block && 
                    o3.custom_stat_block.true_id === minion.stat_block_id));
                  if (potence) {
                    const summon_option = potence.summon_options.find(o3 => 
                      o3.custom_stat_block && 
                      o3.custom_stat_block.true_id === minion.stat_block_id);
                    if (summon_option && summon_option.custom_stat_block) {
                      minion.summon = summon_option.custom_stat_block;
                    }
                  }
                } else {
                  debugger;
                }
              }
            }
            for (const f of minion.actions) {
              if (f.feature) {
                f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
                if (f.feature.floating_feature) {
                  await data_util.loadObject(f.feature.floating_feature);
                }
              }
            }
            for (const f of minion.legendary_actions) {
              if (f.feature) {
                f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
                if (f.feature.floating_feature) {
                  await data_util.loadObject(f.feature.floating_feature);
                }
              }
            }
            for (const f of minion.traits) {
              if (f.feature) {
                f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
                if (f.feature.floating_feature) {
                  await data_util.loadObject(f.feature.floating_feature);
                }
              }
            }
            if (minion.summon || minion.stat_block_id === "") {
              minion.connected = true;
            }
          } else if (minion instanceof ComplexCreatureInstance) {
            const si = minion.summon_instance;
            if (si) {
              const cf = char.features.find(o => o.true_id === si.cf_true_id);
              if (cf && cf.floating_feature) {
                // debugger;
                if (cf.floating_feature instanceof Ability) {
                  const effect = cf.floating_feature.effects.find(o => 
                    o.type === "Summon" && 
                    o.potences.some(o2 => o2.summon_options.some(o3 => 
                      o3.custom_stat_block && 
                      o3.custom_stat_block.true_id === si.stat_block_id)));
                  if (effect) {
                    const potence = effect.potences.find(o2 => o2.summon_options.some(o3 => 
                      o3.custom_stat_block && 
                      o3.custom_stat_block.true_id === si.stat_block_id));
                    if (potence) {
                      const summon_option = potence.summon_options.find(o3 => 
                        o3.custom_stat_block && 
                        o3.custom_stat_block.true_id === si.stat_block_id);
                      if (summon_option && summon_option.custom_stat_block) {
                        si.summon = summon_option.custom_stat_block;
                      }
                    }
                  } else {
                    debugger;
                  }
                }
              }
              for (const f of minion.features) {
                if (f.feature) {
                  f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
                  if (f.feature.floating_feature) {
                    await data_util.loadObject(f.feature.floating_feature);
                  }
                }
              }
              // for (const f of minion.legendary_actions) {
              //   if (f.feature) {
              //     f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
              //     if (f.feature.floating_feature) {
              //       await data_util.loadObject(f.feature.floating_feature);
              //     }
              //   }
              // }
              // for (const f of minion.traits) {
              //   if (f.feature) {
              //     f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
              //     if (f.feature.floating_feature) {
              //       await data_util.loadObject(f.feature.floating_feature);
              //     }
              //   }
              // }
              if (si.summon || si.stat_block_id === "") {
                minion.connected = true;
              }
            } else {
              for (const f of minion.features) {
                if (f.feature) {
                  f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
                  if (f.feature.floating_feature) {
                    await data_util.loadObject(f.feature.floating_feature);
                  }
                }
              }
              // for (const f of minion.legendary_actions) {
              //   if (f.feature) {
              //     f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
              //     if (f.feature.floating_feature) {
              //       await data_util.loadObject(f.feature.floating_feature);
              //     }
              //   }
              // }
              // for (const f of minion.traits) {
              //   if (f.feature) {
              //     f.feature.floating_feature = api.get_floating_feature(f.feature.floating_feature_id);
              //     if (f.feature.floating_feature) {
              //       await data_util.loadObject(f.feature.floating_feature);
              //     }
              //   }
              // }
              minion.connected = true;
            }
          } else if (minion instanceof TransformInstance) {
            // console.log(minion);
            // debugger;
            let res = await api.getFullObject("transform", minion.transform_id);
            if (res instanceof Transform) {
              minion.transform = res;
            }
            if (minion.creature_id !== "") {
              res = await api.getFullObject("creature", minion.creature_id);
              if (res instanceof Creature) {
                minion.creature = res;
                const ci = new BasicCreatureInstance();
                await ci.copyCreature(res, res.challenge_rating);
                minion.creature_instance = ci;
              }
            }
            minion.connected = true;
          } 
        } 
        complete++;
        doProgress();
      }
    }
  }
  connectTransform = async (char: Character) => {
    if (char.transform && (!char.transform.transform || (char.transform.creature_id !== "" && !char.transform.creature))) {
      char.transform.character = char;
      let res = await api.getFullObject("transform", char.transform.transform_id);
      if (res instanceof Transform) {
        // console.log(res);
        char.transform.transform = res;
      }
      if (char.transform.creature_id !== "") {
        res = await api.getFullObject("creature", char.transform.creature_id);
        if (res instanceof Creature) {
          char.transform.creature = res;
          const ci = new BasicCreatureInstance();
          await ci.copyCreature(res, res.challenge_rating);
          char.transform.creature_instance = ci;
        }
      }
    }
  }
  connectEncounterCreatures = async (encounter: Encounter) => {
    const creature_participants = encounter.participants.filter(o => o.participant_type === "Creature");
    const creatures: Creature[] = [];
    for (const participant of creature_participants) {
      for (const ci of participant.child_participants) {
        if (ci instanceof BasicCreatureInstance) {
          await this.connectCreature(ci, creatures);
        }
      }
    }
  }
  connectCreature = async (minion: BasicCreatureInstance, creatures: Creature[]) => {
    if (!minion.connected && minion.creature_id !== "") {
      let creature = creatures.find(o => o._id === minion.creature_id);
      if (!creature) {
        const res = await api.getFullObject("creature", minion.creature_id);
        if (res instanceof Creature) {
          creatures.push(res);
          creature = res;
        }
      }
      if (creature) {
        for (const fb of creature.fbs) {
          for (const f of fb.features) {
            await this.connectCreatureAction(f);
          }
        }
      }
      if (creature) {
        await minion.connectCreature(creature, true);
      }
    } 
  }
  connectCreatureAction = async (action: Feature) => {
    if (action.floating_feature instanceof CreatureSpellAsAbility) {
      const csaf = action.floating_feature;
      if (!csaf.spell) {
        const spell = data_util.spells.filter(o => o._id === csaf.spell_id)[0];
        if (spell) {
          await data_util.loadObject(spell);
          csaf.connectSpell(spell);
        }
      }
    }
  }
  connectFeatureBase = async (cfb: CharacterFeatureBase, char: Character) => {
    for (let j = 0; j < cfb.features.length; ++j) {
      const cf = cfb.features[j];
      if (cf.feature_type === "Feat") {
        let maybe_opt: CharacterFeat | null = null;
        if (cf.feature_options[0] instanceof CharacterFeat) {
          maybe_opt = cf.feature_options[0];
        } else {
          cf.feature_options[0] = new CharacterFeat(cf.feature_options[0]);
          maybe_opt = cf.feature_options[0];
        }
        if (maybe_opt && maybe_opt.source_id !== "") {
          const opt = maybe_opt;
          // There's a mismatch between the sub_cfs on background and the opt.
          // The opt should be the real one though, 
          // but it seems like it's getting stuck in the past 
          // instead of getting updated and used as the source of truth.
          const feat = data_util.feats.find(o => o._id === opt.source_id);
          if (feat) {
            await data_util.loadObject(feat);
            if (opt && opt.copy_source) {
              await opt.copy_source({ char, feature_source: feat, item_id: cf.use_item_id });
            } else {
              debugger;
            }
          }
          if (opt.feat_source_id === "") {
            opt.feat_source_id = opt.root_id;
            opt.feat_source_name = opt.root_name;
            opt.feat_source_type = opt.root_type;
          }
        }
      } else if (cf.feature_type === "Pact Boon") {
        const opt = cf.feature_options[0] as CharacterPactBoon;
        if (opt.source_id !== "") {
          const pact_boons = await data_util.getPactBoons();
          const pact_boon = pact_boons.find(o => o._id === opt.source_id);
          if (pact_boon) {
            await data_util.loadObject(pact_boon);
            await opt.copy_source({ char, feature_source: pact_boon, parent_cf: cf, item_id: cf.use_item_id });
          }
        }
      } else if (cf.feature_type === "Eldritch Invocations" || 
        cf.feature_type === "Fighting Style") {
      } else if (cf.feature_type === "Weapon Mastery") {
        // Handled elsewhere
      } else if (cf.feature_type.includes("Special Feature") && cf.feature_type !== "Special Feature List Merge") {
        for (let i = 0; i < cf.feature_options.length; ++i) {
          if (!(cf.feature_options[i] instanceof CharacterSpecialFeature)) {
            cf.feature_options[i] = new CharacterSpecialFeature(cf.feature_options[i]);
          }
          const opt = cf.feature_options[i];
          if (cf.feature_options[i] instanceof CharacterSpecialFeature) {
            if (opt.source_id !== "") {
              const sf = data_util.special_features.find(o => o._id === opt.source_id);
              if (sf) {
                await data_util.loadObject(sf);
                await opt.copy_source({ char, feature_source: sf, parent_cf: cf, item_id: cf.use_item_id });
              }
            }
          }
        }
      } else {
      }
    }
  }
  connectMagicItemKeyword = async ({
    char, 
    char_item_or_id, 
    magic_item_keyword_or_id,
    source_id = "",
    char_feature_id = ""
  }: {
    char: Character, 
    char_item_or_id: CharacterItem | string, 
    magic_item_keyword_or_id: MagicItemKeyword | string, 
    source_id?: string,
    char_feature_id?: string
  }) => {
    let char_item: CharacterItem | null = null;
    let magic_item_keyword: MagicItemKeyword | null = null;
    if (char_item_or_id instanceof CharacterItem) {
      char_item = char_item_or_id;
    } else {
      const id = char_item_or_id;
      const item_finder = char.items.find(o => o.true_id === id);
      if (item_finder) {
        char_item = item_finder;
      }
    }
    if (magic_item_keyword_or_id instanceof MagicItemKeyword) {
      magic_item_keyword = magic_item_keyword_or_id;
    } else {
      const id = magic_item_keyword_or_id;
      const keyword_finder = data_util.magic_item_keywords.find(o => o._id === id);
      if (keyword_finder) {
        magic_item_keyword = keyword_finder;
      }
    }
    if (char_item && magic_item_keyword) {
      const extra = char_item.extra;
      let extra_spell: Spell | null = null;
      const spell = data_util.spells.find(o => o._id === extra);
      if (spell) {
        await data_util.loadObject(spell);
        extra_spell = spell;
      }
      // debugger;
      await char_item.connectMagicItemKeyword({ 
        owner: char, 
        keyword: magic_item_keyword, 
        class_id: source_id,
        extra_spell,
        char_feature_id
      });
    }
  }
  
  addFeat = async (char: Character, feat: Feat) => {
    const char_feat = new CharacterFeat();
    await data_util.loadObject(feat);
    await char_feat.copy_source({ char, feature_source: feat });
    char.extra_feats.push(char_feat);
    await this.connectCharacter(char);
    char.changed();
    return char_feat;
  }
  removeFeat = async (char: Character, feat: CharacterFeat) => {
    char.extra_feats = char.extra_feats.filter(o => o.true_id !== feat.true_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addGift = async (char: Character, gift: Gift) => {
    await data_util.loadObject(gift);
    const char_gift = new CharacterGift();
    await char_gift.copy_source({ char, feature_source: gift });
    char.gifts.push(char_gift);
    await this.connectCharacter(char);
    char.changed();
    
    return char_gift;
  }
  removeGift = async (char: Character, gift: CharacterGift) => {
    char.gifts = char.gifts.filter(o => o.source_id !== gift.source_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addBastionFacility = async (char: Character, bf: BastionFacility) => {
    const char_bf = new CharacterBastionFacility();
    char_bf.source = bf;
    char_bf.source_id = bf._id;
    char.bastion_facilities.push(char_bf);
    await this.connectCharacter(char);
    char.changed();
    
    char.subscription_holder.bastionChanged();
    return char_bf;
  }
  deleteBastionFacility = async (char: Character, char_bf: CharacterBastionFacility) => {
    char.bastion_facilities = char.bastion_facilities.filter(o => o.true_id !== char_bf.true_id);
    await this.connectCharacter(char);
    char.changed();
    
    char.subscription_holder.bastionChanged();
  }
  addClass = async (char: Character, game_class: GameClass) => {
    await data_util.loadObject(game_class);
    if (char.classes.length === 1 && char.character_level < 1) {
      char.classes[0].level = 1;
      char.classes[0].do_suggestions = true;
    }
    const char_class = new CharacterClass();
    char_class.position = char.classes.length;
    await char_class.copy_source({ char, feature_source: game_class });
    char_class.level = 1;
    char_class.do_suggestions = true;
    char.classes.push(char_class);
    for (let i = 0; i < char_class.all_char_feature_bases.length; ++i) {
      await this.connectFeatureBase(char_class.all_char_feature_bases[i], char);
    }
    await this.connectCharacter(char);
    char.changed();
    
    return char_class;
  }
  removeClass = async (char: Character, char_class: CharacterClass) => {
    char.classes = char.classes.filter(o => o.source_id !== char_class.source_id);
    char.classes.filter(o => o.position > char_class.position).forEach(cc => {
      cc.position--;
      if (cc.position === 0) {
        cc.fixWithGameClass(char);
      }
    });
    char.spells = char.spells.filter(o => o.source_type !== "Class" || o.source_id !== char_class.source_id);
    char.char_feature_bases = char.char_feature_bases.filter(o => 
      o.source_true_id !== char_class.true_id);
    char.classes = char.classes.filter(o => o.source_id !== char_class.source_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addBackground = async (char: Character, background: Background | null) => {
    const char_bg = new CharacterBackground();
    char_bg.source_id = background ? background._id : "";
    if (background) {
      await data_util.loadObject(background);
      await char_bg.copy_source({ char, feature_source: background });
      for (let i = 0; i < char_bg.all_char_feature_bases.length; ++i) {
        await this.connectFeatureBase(char_bg.all_char_feature_bases[i], char);
      }
    }
    char.background = char_bg;
    char.changed();
    await this.connectCharacter(char);
  }
  removeBackground = async (char: Character, char_bg: CharacterBackground) => {
    char.char_feature_bases = char.char_feature_bases.filter(o => 
      o.source_true_id !== char_bg.true_id && 
      o.attached_source_true_id !== char_bg.true_id
    );
    char.background = new CharacterBackground();
    await this.connectCharacter(char);
    char.changed();
  }
  addItem = async ({
    char, 
    item, 
    count = 1,
    char_bf
  }: {
    char: Character, 
    item: Item | PseudoItem | CharacterCustomItem, 
    count?: number,
    char_bf?: CharacterBastionFacility
  }) => {
    let remaining = count;
    let backpack_added: CharacterItem | null = null;
    const use_item = item instanceof PseudoItem && item.base_item ? item.base_item : item;
    if (use_item instanceof Ammunition || use_item instanceof BasicItem || use_item instanceof Weapon) {
      while (remaining > use_item.bundle_size) {
        await this.addItem({ char, item: use_item, count: use_item.bundle_size, char_bf });
        remaining -= use_item.bundle_size;
      }
    }
    if (char.items.length === 0) {
      const items: CharacterItem[] = [];
      const containers: CharacterContainer[] = [];
      const equipment = new CharacterContainer();
      equipment.name = "Equipment";
      equipment.true_id = "Equipment";
      items.push(equipment);
      containers.push(equipment);
      const storage = new CharacterContainer();
      storage.name = "Storage";
      storage.true_id = "Storage";
      items.push(storage);
      containers.push(storage);
      char.items = items;
      char.containers = containers;
      if (use_item.name !== "Backpack") {
        const backpack = data_util.base_pseudo_items.find(o => o.name === "Backpack");
        if (backpack) {
          debugger;
          backpack_added = await this.addItem({ char, item: backpack });
        }
      }
    }
    let char_item: CharacterItem | null = null;
    switch (use_item.item_type) {
      case "Armor":
        char_item = new CharacterArmor();
      break;
      case "Weapon":
        char_item = new CharacterWeapon();
        const char_weapon = new CharacterWeapon();
        char_weapon.count = remaining;
        if (use_item instanceof Weapon) {
          char_weapon.using_attack_types = [...use_item.the_attack_types];
        }
        char_item = char_weapon;
      break;
      case "Ammunition":
        const char_ammo = new CharacterAmmunition();
        char_ammo.count = remaining;
        char_item = char_ammo;
      break;
      case "Container":
        char_item = new CharacterContainer();
        char.containers.push(char_item as CharacterContainer);
      break;
      case "Custom":
        if (use_item instanceof CharacterCustomItem) {
          char_item = use_item;
        } else {
          const char_cust = new CharacterCustomItem();
          char_cust.count = remaining;
          char_item = char_cust;
        }
      break;
      case "Deck":
        const char_deck = new CharacterDeck();
        char_item = char_deck;
      break;
      case "Gem":
        const char_gem = new CharacterGem();
        if (item instanceof GemItem) {
          if (item.sizes.length === 0) {
            char_gem.cost = item.cost;
            char_gem.weight = item.weight;
          } else {
            char_gem.gem_size_id = item.sizes[0].true_id;
          }
        }
        char_item = char_gem;
      break;
      case "Book":
        const char_book = new CharacterBook();
        char_item = char_book;
      break;
      default:
        const char_basic = new CharacterBasicItem();
        char_basic.count = remaining;
        char_item = char_basic;
      break;
    }
    if (char_item) {
      if (item instanceof Item) {
        await char_item.connectBaseItem({ owner: char, base_item: item });
      } else if (item instanceof PseudoItem) {
        await char_item.copyPseudoItem(char, item);
      }
      if (char_bf) {
        char_bf.items.push(char_item);
      } else {
        let added_to_stack = false;
        if (!(char_item instanceof CharacterContainer)) {
          const base_item_id = char_item.base_item_id;
          let good_containers = char.containers.filter(o => o.base_item_ids.includes(base_item_id));
          if (good_containers.length === 0) {
            const item_type = char_item.item_type;
            good_containers = char.containers.filter(o => o.item_types.includes(item_type));
          }
          const backpack = char.containers.filter(o => o.name === "Backpack");
          const equipment = char.containers.filter(o => o.name === "Equipment");
          if (good_containers.length > 0) {
            if (isNaN(good_containers[0].content_weight)) {
              good_containers[0].content_weight = 0;
            }
            if (good_containers[0].max_weight === -1 || (good_containers[0].content_weight + char_item.weight) < good_containers[0].max_weight) {
              char_item.in_char_container_id = good_containers[0].true_id;
              good_containers[0].content_weight += char_item.weight;
            } else if (backpack.length > 0 && (backpack[0].max_weight === -1 || (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight)) {
              char_item.in_char_container_id = backpack[0].true_id;
              backpack[0].content_weight += char_item.weight;
            } else {
              char_item.in_char_container_id = "Equipment";
              if (char_item.weight && char_item.weight > 0) {
                equipment[0].content_weight += char_item.weight;
              }
            }
          } else if (!["Weapon","Armor"].includes(char_item.item_type) && 
            !char_item.item_type.includes("Focus") && 
            !char_item.name.includes("Clothes") && 
            backpack.length > 0 && 
            (backpack[0].max_weight === -1 || 
              (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight
            )
          ) {
            char_item.in_char_container_id = backpack[0].true_id;
            backpack[0].content_weight += char_item.weight;
          } else {
            char_item.in_char_container_id = "Equipment";
            if (char_item.weight && char_item.weight > 0) {
              equipment[0].content_weight += char_item.weight;
            }
          }
          if (char_item.stackable) {
            // Find a stack to join it with
            const in_char_container_id = char_item.in_char_container_id;
            const bundle_size = char_item.bundle_size;
            const stack = char.items.filter(o => 
              o.base_item_id === base_item_id && 
              o.in_char_container_id === in_char_container_id && 
              o.count + remaining <= bundle_size)[0];
            if (stack && (stack instanceof CharacterBasicItem || 
              stack instanceof CharacterAmmunition || 
              stack instanceof CharacterWeapon)) {
              stack.count += remaining;
              char_item = stack;
              added_to_stack = true;
            }
          }
        }
        if (!added_to_stack) {
          char.items.push(char_item);
        }
      }
      if (item instanceof PseudoItem && item.extra !== "") {
        char_item.extra = item.extra;
        char_item.extra_spell = item.extra_spell;
      }
    }
    await this.connectItem(char, char_item);
    if (backpack_added) {
      const backpacks = char.items.filter(o => o.name === "Backpack");
      if (backpacks.length > 1) {
        // Extra backpack.  One should be empty.  Delete it.
        for (const bp of backpacks) {
          // Check if it's empty
          if (char.items.filter(o => o.in_char_container_id === bp.true_id).length === 0) {
            // It's empty.  Delete it.
            this.removeItem(char, bp);
            break;
          }
        }
      }
    }
    char.subscription_holder.itemsChanged();
    char.changed();
    return char_item;
  }
  removeItem(char: Character, item: CharacterItem) {
    let doItemsChanged = true;
    if (item.attuned) {
      this.unattuneItem(char, item);
      doItemsChanged = false;
    }
    if (item.equipped) {
      this.unequipItem(char, item);
      doItemsChanged = false;
    }
    char.items = char.items.filter(o => o.true_id !== item.true_id);
    if (item instanceof CharacterContainer) {
      char.containers = char.containers.filter(o => o.true_id !== item.true_id);
      char.items.filter(o => o.in_char_container_id === item.true_id).forEach(item2 => {
        item2.in_char_container_id = "Equipment";
      });
    } else {
      if (item.in_char_container_id === "Equipment") {
        if (item.weight && item.weight > 0) {
          char.equipment_container.content_weight -= item.weight;
        }
      } else {
        const container_finder = char.containers.filter(o => o.true_id === item.in_char_container_id);
        if (container_finder.length === 1) {
          const container = container_finder[0];
          if (item.weight && item.weight > 0) {
            container.content_weight -= item.weight;
          }
        }
      }
    }
    char.changed();
    if (doItemsChanged) {
      char.subscription_holder.itemsChanged();
    }
  }
  moveItem(char: Character, item: CharacterItem, container_id: string) {
    let container_finder = item.in_char_container_id === "Equipment" ? [] : char.containers.filter(o => o.true_id === item.in_char_container_id);
    if (container_finder.length === 1) {
      const container = container_finder[0];
      if (item.weight && item.weight > 0) {
        container.content_weight -= item.weight;
      }
    } else {
      if (item.weight && item.weight > 0) {
        char.equipment_container.content_weight -= item.weight;
      }
    }
    item.in_char_container_id = container_id;
    container_finder = char.containers.filter(o => o.true_id === item.in_char_container_id);
    if (container_finder.length === 1) {
      const container = container_finder[0];
      if (item.weight && item.weight > 0) {
        container.content_weight += item.weight;
      }
    } else {
      if (item.weight && item.weight > 0) {
        char.equipment_container.content_weight += item.weight;
      }
    }
    char.changed();
    char.subscription_holder.itemsChanged();
  }
  equipItem(char: Character, item: CharacterItem): void {
    if (item.base_item && item.base_item.item_type === "Armor" && item.base_item instanceof Armor) {
      if (item.base_item.armor_type_name === "Shield") {
        if (!char.shield_carried) {
          item.equipped = true;
          char.equipped_items.push(item);
        }
      } else if (!char.armor_worn) {
        item.equipped = true;
        char.equipped_items.push(item);
      }
    } else {
      item.equipped = true;
      char.equipped_items.push(item);
    }
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  unequipItem(char: Character, item: CharacterItem): void {
    if (item.base_item && item.base_item.item_type === "Armor" && item.base_item instanceof Armor) {
      if (item.base_item.armor_type_name === "Shield") {
        item.equipped = false;
        char.equipped_items = char.equipped_items.filter(o => o.true_id !== item.true_id);
      } else {
        console.log('I think something isnt quite right here.');
        console.log("It's not always setting armor_worn to false when it should");
        item.equipped = false;
        char.equipped_items = char.equipped_items.filter(o => o.true_id !== item.true_id);
      }
    } else {
      item.equipped = false;
      char.equipped_items = char.equipped_items.filter(o => o.true_id !== item.true_id);
    }
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  attuneItem(char: Character, item: CharacterItem): void {
    if (char.attuned_items.length < char.max_attuned_items) {
      item.attuned = true;
      char.attuned_items.push(item);
      char.changed();
      char.subscription_holder.itemsChanged();
      char.subscription_holder.recalculate();
    }
  }
  unattuneItem(char: Character, item: CharacterItem): void {
    item.attuned = false;
    char.attuned_items = char.attuned_items.filter(o => o.true_id !== item.true_id);
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  addSpell = async (char: Character, spell: Spell | CharacterSpell, 
    source: CharacterClass | CharacterFeat | CharacterItem, extra = false) => {
    const char_spell = spell instanceof Spell ? new CharacterSpell() : spell;
    if (spell instanceof Spell) {
      await data_util.loadObject(spell);
      char_spell.copySpell(spell);
      char_spell.connectSource(source);
    }
    char.spells.push(char_spell);
    
    if (source instanceof CharacterClass) {
      if (extra) {
        source.spellcasting.extra_spells.push(char_spell);
      } else {
        source.spellcasting.spells.push(char_spell);
      }
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  removeSpell = async (char: Character, char_spell: CharacterSpell, source_type: string, source_id: string) => {
    if (source_type === "Class") {
      const char_class = char.classes.find(o => o.source_id === source_id);
      if (char_class) {
        char_class.spellcasting.spells = char_class.spellcasting.spells.filter(o => o.true_id !== char_spell.true_id);
        char_class.spellcasting.extra_spells = char_class.spellcasting.extra_spells.filter(o => o.true_id !== char_spell.true_id);
      }
    }
    char.spells = char.spells.filter(o => o.true_id !== char_spell.true_id);
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  addSpellToBook = async (char: Character, spell: Spell | CharacterSpell, 
    source: CharacterClass, extra: boolean) => {
    if (source.spellbook) {
      const char_spellbook = source.spellbook;
      const char_spell = spell instanceof Spell ? new CharacterSpell() : spell;
      if (spell instanceof Spell) {
        await data_util.loadObject(spell);
        char_spell.copySpell(spell);
        char_spell.connectSource(source);
      }
      char_spell.prepared = false;
      
      if (extra) {
        char_spell.extra = true;
      } else {
        char_spell.extra = false;
      }
      char_spellbook.spells.push(char_spell);
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  removeSpellFromBook = async (char: Character, char_spell: CharacterSpell,
    source: CharacterClass) => {
    if (source.spellcasting.spellbook) {
      source.spellcasting.spellbook.spells = source.spellcasting.spellbook.spells.filter(o => o.true_id !== char_spell.true_id);
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  connectCampaign2(char: Character, campaign: Campaign) {
    char.old_campaign_id = campaign._id;
    if (char.c_char && !char.c_char.campaign && char.c_char.campaign_id === campaign._id) {
      char.c_char.campaign = campaign;
    }
  }
}
